// CategoryPickerSheet — Kategorie-Ziel wählen (Karte verschieben, Batch-Verschieben).
// Jede Zeile schließt das Sheet selbst und meldet die Wahl danach nach außen, damit der
// Aufrufer nur den Ziel-Wert verarbeitet.
import React from 'react';
import { Crown, ListPlus, Wand2 } from 'lucide-react';
import BottomSheet from '../../../../ui/BottomSheet';
import List from '../../../../ui/List';
import Badge from '../../../../ui/Badge';
import { formatCategoryName } from '../../../../ui/formatCategoryName';

/**
 * @param {object[]} categories — Kategorien aus useDeckViewState (`{id, name, excluded,
 *   isCommander}`); die Commander-Kategorie selbst wird nicht gelistet, sie hat die eigene
 *   Zeile „Commander".
 * @param {(target: string|number|null) => void} onSelect — Kategorie-ID, `null` für den
 *   Typ-Fallback des Servers oder `'auto'` (nur bei `showAutomatic`).
 * @param {() => void} onSelectCommander — Zeile „Commander" vor „Neue Kategorie…"; ohne
 *   Callback entfällt sie.
 * @param {() => void} onCreateNew — „Neue Kategorie…"; der Aufrufer öffnet den
 *   AddCategoryDialog und nimmt die angelegte Kategorie als Ziel.
 */
export default function CategoryPickerSheet({
  open,
  onOpenChange,
  categories = [],
  onSelect,
  showAutomatic = false,
  onSelectCommander,
  onCreateNew,
  title = 'Kategorie wählen',
}) {
  const choose = (handler) => () => {
    onOpenChange?.(false);
    handler();
  };

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title={title}>
      <List>
        {showAutomatic && (
          <List.Item onClick={choose(() => onSelect?.('auto'))}>
            <List.Main>
              <List.Name>
                <span className="mobile-category-picker__row">
                  <Wand2 size={14} aria-hidden /> Automatisch
                </span>
              </List.Name>
              <List.Meta>Passende Kategorie wird ermittelt</List.Meta>
            </List.Main>
          </List.Item>
        )}
        {categories
          .filter((category) => !category.isCommander)
          .map((category) => (
            <List.Item
              key={category.id ?? category.name}
              onClick={choose(() => onSelect?.(category.id ?? null))}
            >
              <List.Main>
                <List.Name>{formatCategoryName(category.name)}</List.Name>
              </List.Main>
              {category.excluded && (
                <List.Actions>
                  <Badge tone="pending">Nicht im Deck</Badge>
                </List.Actions>
              )}
            </List.Item>
          ))}
        {typeof onSelectCommander === 'function' && (
          <List.Item onClick={choose(onSelectCommander)}>
            <List.Main>
              <List.Name>
                <span className="mobile-category-picker__row">
                  <Crown size={14} aria-hidden /> Commander
                </span>
              </List.Name>
            </List.Main>
          </List.Item>
        )}
        {typeof onCreateNew === 'function' && (
          <List.Item onClick={choose(onCreateNew)}>
            <List.Main>
              <List.Name>
                <span className="mobile-category-picker__row">
                  <ListPlus size={14} aria-hidden /> Neue Kategorie…
                </span>
              </List.Name>
            </List.Main>
          </List.Item>
        )}
      </List>
    </BottomSheet>
  );
}
